import React, { useState, useEffect, useRef } from 'react';
import toast from 'react-hot-toast';
import {
  Clock, Share2, Volume2, VolumeX, Facebook, Twitter, MessageCircle,
  ChevronLeft, ChevronRight,
} from 'lucide-react';
import AuthorAvatar from '../common/AuthorAvatar';
import Watermark from '../common/Watermark';
import SectionHeading from '../common/SectionHeading';
import NewsCard from '../common/NewsCard';
import LatestNews from '../common/LatestNews';
import AdBanner from '../common/AdBanner';
import CommentSection from './CommentSection';
import { useSiteSettings } from '../../hooks/useSiteSettings';
import { CATEGORY_COLOR, CATEGORY_LABEL } from '../../constants';
import { formatPublishNp } from '../../utils/timeAgo';
import { parseRichContent, stripRichMarkers, renderInlineSpans } from '../../utils/richText';

const CHARS_PER_LINE = 70; // Estimated characters per line (ad placement ko lagi)

// Pick a voice that can actually read Devanagari: Nepali first, Hindi as fallback.
// Gender-aware — purush/mahila swar chhutta rojna milchha.
let cachedVoices = [];
const loadTtsVoices = () => {
  if ('speechSynthesis' in window) cachedVoices = window.speechSynthesis.getVoices() || [];
};

const DEV_LANG_RE = /^(ne|hi|mr|sa)([-_]|$)/i;
const NEPALI_RE = /^ne([-_]|$)/i;
// Windows/Chrome/Android ko common Indic voice names bata gender chinchha
const FEMALE_NAME_RE = /(female|woman|girl|kalpana|swara|sarika|lekha|asha|geeta|neerja|sweta|heera|priya|aditi|isha|google\s*(hindi|हिन्दी)|हिन्दी)/i;
const MALE_NAME_RE = /(male|man|boy|hemant|madhur|ravi|prabhat|amit|vikram)/i;

function pickVoiceByGender(gender) {
  const dev = cachedVoices.filter((v) => DEV_LANG_RE.test(v.lang || ''));
  const nepali = dev.filter((v) => NEPALI_RE.test(v.lang || ''));

  const findByGender = (list) =>
    gender === 'female'
      ? list.find((v) => FEMALE_NAME_RE.test(v.name)) || list[0]
      : list.find((v) => MALE_NAME_RE.test(v.name)) || list.find((v) => !FEMALE_NAME_RE.test(v.name)) || null;

  // Real Nepali voice bhete tyahi (native uccharan), natra Hindi fallback
  const np = findByGender(nepali);
  if (np) return { voice: np, lang: np.lang || 'ne-NP', native: 'ne', exactGender: gender === 'female' ? FEMALE_NAME_RE.test(np.name) : MALE_NAME_RE.test(np.name) };

  const hi = findByGender(dev);
  if (hi) return { voice: hi, lang: hi.lang || 'hi-IN', native: 'hi', exactGender: gender === 'female' ? FEMALE_NAME_RE.test(hi.name) : MALE_NAME_RE.test(hi.name) };

  return { voice: null, lang: 'ne-NP', native: 'ne', exactGender: false };
}

function PhotoSlider({ images, videoSrc, alt }) {
  const [index, setIndex] = useState(0);
  const touchX = useRef(null);
  // Photos first ("upside"), then videos at the end — all share the same dots/arrows
  const slides = [
    ...images.map((src) => ({ type: 'image', src })),
    ...(videoSrc ? [{ type: 'video', src: videoSrc }] : []),
  ];
  const n = slides.length;

  const go = (dir) => setIndex((i) => (i + dir + n) % n);

  const onTouchStart = (e) => {
    touchX.current = e.touches[0].clientX;
  };

  const onTouchEnd = (e) => {
    if (touchX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchX.current;
    if (dx > 40) go(-1);
    else if (dx < -40) go(1);
    touchX.current = null;
  };

  if (!n) return null;

  return (
    <div
      className="relative rounded-xl overflow-hidden mb-6 select-none bg-paper-tint"
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      <div
        className="flex transition-transform duration-300 ease-out"
        style={{ transform: `translateX(-${index * 100}%)` }}
      >
        {slides.map((s, i) =>
          s.type === 'video' ? (
            <div key={i} className="relative w-full flex-shrink-0 flex items-center justify-center bg-paper-tint">
              {/* Full video — crop nagari natural size ma */}
              <video
                src={s.src}
                controls
                preload="metadata"
                className="w-full h-auto max-h-[480px] object-contain bg-black"
              />
              {/* Center watermark — mini size */}
              {i === index && <Watermark size={28} opacity={0.5} />}
            </div>
          ) : (
            <div key={i} className="relative w-full flex-shrink-0 flex items-center justify-center bg-paper-tint">
              {/* Full photo — ups ra downs bata ali katayera (object-cover) fitted */}
              <img
                src={s.src}
                alt={`${alt} — फोटो ${i}`}
                loading={i === 0 ? 'eager' : 'lazy'}
                draggable={false}
                className="w-full h-auto max-h-[480px] min-h-[200px] sm:min-h-[280px] max-sm:object-contain object-cover"
              />
              {/* Center watermark — mini size */}
              {i === index && <Watermark position="center" size={50} opacity={2} />}
            </div>
          )
        )}
      </div>

      {n > 1 && (
        <>
          <button
            aria-label="अघिल्लो फोटो"
            onClick={() => go(-1)}
            className="absolute top-1/2 -translate-y-1/2 left-3 w-9 h-9 rounded-full bg-black/40 text-white flex items-center justify-center backdrop-blur-sm hover:bg-crimson transition-colors"
          >
            <ChevronLeft size={18} />
          </button>
          <button
            aria-label="पछिल्लो फोटो"
            onClick={() => go(1)}
            className="absolute top-1/2 -translate-y-1/2 right-3 w-9 h-9 rounded-full bg-black/40 text-white flex items-center justify-center backdrop-blur-sm hover:bg-crimson transition-colors"
          >
            <ChevronRight size={18} />
          </button>
          <div className="absolute bottom-3 left-0 right-0 flex items-center justify-center gap-1.5">
            {slides.map((s, i) => (
              <button
                key={i}
                aria-label={s.type === 'video' ? 'भिडियो' : `फोटो ${i + 1}`}
                onClick={() => setIndex(i)}
                className={`h-2 rounded-full transition-all ${
                  i === index ? 'w-5 bg-white' : 'w-2 bg-white/55 hover:bg-white/85'
                }`}
              />
            ))}
          </div>
          <span className="absolute top-3 right-3 bg-black/55 text-white text-[11px] font-bold px-2 py-0.5 rounded-full font-mono">
            {index + 1}/{n}
          </span>
        </>
      )}
    </div>
  );
}

function ArticleView({ item, allNews, onOpen }) {
  const settings = useSiteSettings();
  const [speaking, setSpeaking] = useState(false);
  const [voicePref, setVoicePref] = useState(null); // 'male' | 'female'

  // Warm up the voice list (Chrome loads voices asynchronously)
  useEffect(() => {
    if ('speechSynthesis' in window) {
      loadTtsVoices();
      window.speechSynthesis.addEventListener('voiceschanged', loadTtsVoices);
    }
    return () => {
      if ('speechSynthesis' in window) {
        window.speechSynthesis.removeEventListener('voiceschanged', loadTtsVoices);
        window.speechSynthesis.cancel();
      }
    };
  }, []);

  if (!item) return null;

  const recent = allNews.filter((n) => n.id !== item.id);
  const related = recent.slice(0, 6);
  const moreNews = recent.slice(0, 6);
  const latestSide = recent.slice(0, 5);

  const parsePhotos = (value) => {
    if (!value) return [];
    if (Array.isArray(value)) return value;
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  };

  const photos = parsePhotos(item.photos);
  const categorySlug = item.category_slug || item.category_id;
  const categoryColor = CATEGORY_COLOR[categorySlug] || 'var(--crimson)';
  const categoryLabel = CATEGORY_LABEL[categorySlug] || 'समाचार';
  const url = window.location.href;

  const openShare = (link) => {
    window.open(link, '_blank', 'noopener,noreferrer');
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success('लिङ्क कपी भयो।');
    } catch (err) {
      toast.error('लिङ्क कपी गर्न सकिएन।');
    }
  };

  const share = () => {
    if (navigator.share) {
      navigator.share({ title: item.title, url }).catch(() => {});
    } else {
      copyLink();
    }
  };

  const stopSpeaking = () => {
    if ('speechSynthesis' in window) window.speechSynthesis.cancel();
    setSpeaking(false);
    setVoicePref(null);
  };

  const speak = (gender = 'female') => {
    if (!('speechSynthesis' in window)) {
      toast.error('यो ब्राउजरमा आवाज सुविधा उपलब्ध छैन।');
      return;
    }
    window.speechSynthesis.cancel();
    setVoicePref(gender);

    const fullText = `${item.title}। ${item.subtitle || ''}। ${stripRichMarkers(item.content || item.excerpt || '')}`;

    // Split into sentence chunks so long articles are read fully
    // (browsers silently cut off long utterances).
    const sentences = fullText.split('।').map((s) => s.trim()).filter(Boolean);
    const chunks = [];
    let cur = '';
    for (const s of sentences) {
      if ((cur + '। ' + s).length > 180 && cur) {
        chunks.push(cur + '।');
        cur = s;
      } else {
        cur = cur ? `${cur}। ${s}` : s;
      }
    }
    if (cur) chunks.push(cur.endsWith('।') ? cur : `${cur}।`);
    if (chunks.length === 0) return;

    loadTtsVoices();
    const { voice, lang, native, exactGender } = pickVoiceByGender(gender);
    // Native Nepali voice ma ali dhilo ra natural; Hindi fallback ma slight tune
    let rate = native === 'ne' ? 0.92 : 0.88;
    // Male rojna sakinena bhane (system ma female matra cha) pitch ghataera purush jasto suniyeka
    const pitch = gender === 'female' ? 1.05 : exactGender ? 0.85 : 0.55;
    if (gender === 'male' && !exactGender) rate -= 0.06;

    let finished = 0;
    const markDone = () => {
      finished += 1;
      if (finished >= chunks.length) {
        setSpeaking(false);
        setVoicePref(null);
      }
    };

    setSpeaking(true);

    // Mobile browsers (Android Chrome) ma cancel() pachi turuntai speak() garda
    // silent hune race bug hunchha — 150ms pachi schedule garne + volume explicit
    setTimeout(() => {
      try { window.speechSynthesis.resume(); } catch { /* noop */ }
      chunks.forEach((chunk) => {
        const u = new SpeechSynthesisUtterance(chunk);
        u.lang = lang;
        if (voice) u.voice = voice;
        u.rate = rate;
        u.pitch = pitch;
        u.volume = 1;
        u.onend = markDone;
        u.onerror = markDone;
        window.speechSynthesis.speak(u);
      });
    }, 150);
  };

  const shareText = encodeURIComponent(`${item.title}\n${url}`);

  // Admin le footer settings bata customize garna sakne — default sharer links
  const s = settings || {};
  const shareButtons = [
    { Icon: Facebook, label: 'Facebook', cls: 'bg-[#1877F2] hover:bg-[#145DBF]', onClick: () => openShare(s.share_facebook || `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`) },
    { Icon: Twitter, label: 'Twitter', cls: 'bg-[#1DA1F2] hover:bg-[#0C8BD9]', onClick: () => openShare(s.share_twitter || `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${shareText}`) },
    { Icon: MessageCircle, label: 'WhatsApp', cls: 'bg-[#25D366] hover:bg-[#1DA851]', onClick: () => openShare(s.share_whatsapp || `https://wa.me/?text=${shareText}`) },
    { Icon: MessageCircle, label: 'Viber', cls: 'bg-[#7360F2] hover:bg-[#5B4AD4]', onClick: () => openShare(s.share_viber || `viber://forward?text=${shareText}`) },
  ];

  return (
    <main className="page max-w-[1240px] mx-auto px-5 pt-7 pb-[60px] w-full">
      {/* Share bar */}
      <div className="flex items-center justify-center gap-2.5 flex-wrap pb-6 mb-6 border-b border-line">
        <span className="text-[13px] font-semibold text-muted mr-1">सेयर गर्नुहोस्:</span>
        {shareButtons.map(({ Icon, label, cls, onClick }) => (
          <button
            key={label}
            aria-label={label}
            title={label}
            onClick={onClick}
            className={`p-2 text-white rounded-xl transition ${cls}`}
          >
            <Icon size={18} />
          </button>
        ))}
        <button
          aria-label="Share"
          title="सेयर"
          onClick={share}
          className="p-2 bg-gray-900 text-white rounded-xl hover:bg-gray-800 transition"
        >
          <Share2 size={18} />
        </button>
      </div>

      <div className="grid gap-8 lg:grid-cols-[1fr_320px] items-start">
        {/* Main article */}
        <div className="min-w-0">
          <article className="bg-white border border-line rounded-2xl shadow-sm overflow-hidden">
            <div className="p-5 sm:p-8">
              <div className="flex items-center gap-1.5 text-[12.5px] font-bold text-crimson mb-3">
                <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: categoryColor }} /> {categoryLabel}
              </div>

              <h1 className="font-display text-[clamp(22px,3.6vw,38px)] leading-tight mb-3">{item.title}</h1>
              {item.subtitle && <p className="text-base sm:text-lg text-ink-soft mb-5 leading-relaxed">{item.subtitle}</p>}

              <div className="flex flex-wrap items-center gap-x-5 gap-y-3 py-4 mb-6 border-y border-line">
                {/* Author — click garda टिम (hamro-tim) page ma janchha */}
                <button
                  type="button"
                  className="flex items-center gap-2.5 rounded-full pr-3 pl-0.5 py-0.5 -ml-0.5 hover:bg-paper-soft transition-colors group/author"
                  onClick={() => window.location.assign('/hamro-tim')}
                  title={`${item.author} — हाम्रो टिम`}
                >
                  <AuthorAvatar name={item.author} imgSrc={item.author_image} size={34} />
                  <span className="text-ink-soft font-semibold text-[13.5px] group-hover/author:text-blue transition-colors underline-offset-2 group-hover/author:underline">
                    {item.author}
                  </span>
                </button>
                <div className="flex items-center gap-1.5 text-[13px] text-muted">
                  <Clock size={14} /> <span>{formatPublishNp(item.created_at || item.date)}</span>
                </div>
                {item.read_time && (
                  <div className="flex items-center gap-1.5 text-[13px] text-muted">
                    <span className="opacity-60">•</span> <span>{item.read_time} मिनेट पढाइ</span>
                  </div>
                )}
                {/* Speaker — female voice (mobile-safe scheduling) */}
                <div className="ml-auto flex items-center gap-2">
                  {!speaking && (
                    <button
                      type="button"
                      className="w-10 h-10 rounded-full bg-crimson/10 text-crimson hover:bg-crimson hover:text-white flex items-center justify-center transition-colors"
                      onClick={() => speak('female')}
                      aria-label="सुन्नुहोस्"
                      title="समाचार सुन्नुहोस्"
                    >
                      <Volume2 size={18} />
                    </button>
                  )}

                  {speaking && (
                    <>
                      <span className="inline-flex items-center gap-1.5 text-[12.5px] font-bold px-3.5 py-2 rounded-full bg-crimson text-white animate-pulse">
                        <Volume2 size={15} /> पढँदैछ...
                      </span>
                      <button
                        type="button"
                        className="w-10 h-10 rounded-full bg-gray-900 text-white hover:bg-gray-800 flex items-center justify-center transition-colors"
                        onClick={stopSpeaking}
                        aria-label="रोक्नुहोस्"
                        title="रोक्नुहोस्"
                      >
                        <VolumeX size={17} />
                      </button>
                    </>
                  )}
                </div>
              </div>

              {(() => {
                const slides = [item.image, ...photos]
                  .filter(Boolean)
                  .filter((v, i, a) => a.indexOf(v) === i);
                return <PhotoSlider images={slides} videoSrc={item.video} alt={item.title} />;
              })()}

              {/* Article top ad - appears after photo/video slider */}
              <AdBanner position="article-top" maxAds={2} showAll={false} />

              {(() => {
                // Rich content — **bold**, *italic*, >> center ra • ○ ▪ ► bullets
                // Full article dekhauchha — home/listing ma mini description, tara detail ma पूरा
                const paragraphs = parseRichContent(item.content || item.excerpt || '');
                if (paragraphs.length === 0) return null;

                // Calculate estimated lines for each paragraph (ad placement ko lagi)
                const getLineCount = (p) => Math.max(1, Math.ceil(p.text.length / CHARS_PER_LINE));
                const paragraphLines = paragraphs.map(getLineCount);
                const totalLines = paragraphLines.reduce((sum, lines) => sum + lines, 0);

                // Find where to insert inline ads (every ~15 lines, max 3 ads)
                const AD_INTERVAL_LINES = 15;
                const MAX_INLINE_ADS = 3;
                const adPositions = [];
                let cumulativeLines = 0;
                let adCount = 0;

                paragraphs.forEach((p, idx) => {
                  cumulativeLines += paragraphLines[idx];
                  if (adCount < MAX_INLINE_ADS &&
                      cumulativeLines >= AD_INTERVAL_LINES * (adCount + 1) &&
                      idx < paragraphs.length - 1 &&
                      cumulativeLines < totalLines - 5) {
                    adPositions.push(idx);
                    adCount++;
                  }
                });

                const renderParagraph = (p, keyPrefix) => {
                  if (p.bullet) {
                    return (
                      <div key={keyPrefix} className="flex gap-2.5 mb-3 leading-[1.85]">
                        <span className="flex-shrink-0 font-bold text-emerald-700 mt-[2px]">{p.bullet}</span>
                        <span className={`flex-1 ${p.align === 'center' ? 'text-center' : 'text-justify'}`}>
                          {renderInlineSpans(p.spans)}
                        </span>
                      </div>
                    );
                  }
                  return (
                    <p key={keyPrefix} className={`mb-4 leading-[1.85] ${p.align === 'center' ? 'text-center' : 'text-justify'}`}>
                      {renderInlineSpans(p.spans)}
                    </p>
                  );
                };

                const renderParagraphsWithAds = (paras, lineCounts, adPos) => {
                  return paras.map((p, idx) => {
                    const shouldShowAd = adPos.includes(idx);
                    return (
                      <React.Fragment key={`para-${idx}`}>
                        {renderParagraph(p, `para-${idx}`)}
                        {shouldShowAd && (
                          <div className="my-4">
                            <AdBanner
                              position="inarticle"
                              noFallback
                              maxAds={2}
                              showAll={false}
                            />
                          </div>
                        )}
                      </React.Fragment>
                    );
                  });
                };

                return (
                  <div className="text-base sm:text-[17px] leading-[1.85] text-ink-soft">
                    {renderParagraphsWithAds(paragraphs, paragraphLines, adPositions)}
                  </div>
                );
              })()}

              {/* लेखको अन्त्यमा — ad */}
              <div className="mt-8">
                <AdBanner position="lekh_last" maxAds={1} showAll={false} />
              </div>

              <div className="mt-10 pt-6 border-t-2 border-line">
                <CommentSection articleId={item.id} />
              </div>
            </div>
          </article>
        </div>

        {/* Sidebar */}
        <aside className="flex flex-col gap-6 lg:sticky lg:top-24 min-w-0">
          {/* सम्बन्धित समाचार माथि — ad */}
          <AdBanner position="related_top" maxAds={1} showAll={false} />

          {related.length > 0 && (
            <div className="bg-white border border-line rounded-2xl shadow-sm p-5">
              <h3 className="font-display text-[18px] font-extrabold flex items-center gap-2.5 mb-4 pb-2.5 border-b-2 border-line">
                <span className="inline-block w-[20px] h-[4px] rounded-full bg-crimson" />
                सम्बन्धित समाचार
              </h3>
              <div className="flex flex-col gap-4">
                {related.map((n) => {
                  const nImage = n.image || parsePhotos(n.photos)[0] || null;
                  return (
                    <article
                      key={n.id}
                      className="group flex gap-3 cursor-pointer"
                      onClick={() => onOpen(n)}
                      role="button"
                      tabIndex={0}
                    >
                      <img
                        src={nImage || 'https://via.placeholder.com/400x300?text=Kalo+Jhola'}
                        alt={n.title}
                        loading="lazy"
                        className="w-24 h-[70px] object-cover rounded-lg flex-shrink-0 transition-opacity group-hover:opacity-80"
                      />
                      <div className="min-w-0">
                        <h4 className="text-[13.5px] font-semibold leading-snug transition-colors group-hover:text-blue line-clamp-3">
                          {n.title}
                        </h4>
                        <span className="flex items-center gap-1 text-xs text-muted mt-1">
                          <Clock size={11} /> {formatPublishNp(n.created_at || n.date)}
                        </span>
                      </div>
                    </article>
                  );
                })}
              </div>
            </div>
          )}

          {/* सम्बन्धित समाचार तल — ad */}
          <AdBanner position="related_bottom" maxAds={1} showAll={false} />

          <LatestNews items={latestSide} onOpen={onOpen} />
          <AdBanner position="sidebar" />
        </aside>
      </div>

      {/* More news */}
      <section className="mt-14">
        <SectionHeading title="थप समाचारहरू" />
        <div className="grid gap-7 grid-cols-1 md:grid-cols-2 lg:grid-cols-3 w-full">
          {moreNews.map((n) => (
            <NewsCard key={n.id} item={n} onOpen={onOpen} />
          ))}
        </div>
      </section>
    </main>
  );
}

export default ArticleView;